import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { _log } from '@shared/aux_helper_environment';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { _formatColumnTitle } from './_aux_dataBricksAiGenieQueryProcessTable';

interface ColumnSelectorData {
  columns: string[];
  numericColumns: string[];
  filteredData: string[];
  // Sugerencias precargadas en el form. Si están presentes y son válidas (existen en
  // las listas filtradas) prevalecen sobre las heurísticas locales.
  suggestedLabelColumn?: string | null;
  suggestedNumericColumns?: string[] | null;
}

@Component({
  selector: 'column-selector-dialog',
  template: `
    <h2 mat-dialog-title>{{ getTitle() }}</h2>
    <div mat-dialog-content *ngIf="init">
      <form [formGroup]="form">
        <div class="column-selectors">
          <mat-form-field class="full-width">
            <mat-label>{{ getLabelColumnText() }}</mat-label>
            <mat-select formControlName="labelColumn">
              <mat-option *ngFor="let col of getLabelColumns()" [value]="col">
                {{ formatColumnTitle(col) }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>{{ getNumericColumnText() }}</mat-label>
            <mat-select formControlName="numericColumns" #numericSelect [multiple]="multiSelectEnabled">
              <mat-option *ngFor="let col of getNumericColumns()" [value]="col">
                {{ formatColumnTitle(col) }}
              </mat-option>
            </mat-select>
            <mat-hint *ngIf="multiSelectEnabled">{{ getMultiSeriesHint() }}</mat-hint>
          </mat-form-field>
        </div>
      </form>
    </div>
    <div mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">{{ getCancelText() }}</button>
      <button mat-raised-button color="accent" [disabled]="!form.valid" (click)="onConfirm()">{{ getVisualizeText() }}</button>
    </div>
  `,
  styles: [
    `
      .column-selectors {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .full-width {
        width: 100%;
      }
    `,
  ],
})
export class ColumnSelectorDialogComponent implements OnInit {
  @ViewChild('numericSelect') numericSelect: any;

  form: FormGroup;
  init = false;
  multiSelectEnabled = false;

  private _cachedLabelColumns: string[] = null;
  private _cachedNumericColumns: string[] = null;

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: ColumnSelectorData,
    private dialogRef: MatDialogRef<ColumnSelectorDialogComponent>,
    private translate: IanTranslateService
  ) {}

  ngOnInit(): void {
    const numericCols = this.getNumericColumns();
    const labelCols = this.getLabelColumns();
    this.multiSelectEnabled = numericCols.length >= 2;

    // Label sugerido: usar el de data si está disponible y es válido, sino heurística local.
    const suggestedLabel =
      this.data.suggestedLabelColumn && labelCols.includes(this.data.suggestedLabelColumn)
        ? this.data.suggestedLabelColumn
        : this.findDefaultLabelColumn();

    // Numerics sugeridas: filtrar solo las que están disponibles después del filtrado interno;
    // si hay varias y multi está habilitado, pre-seleccionar todas (max 2 por defecto).
    const suggestedNumerics = (this.data.suggestedNumericColumns || []).filter(c => numericCols.includes(c));

    let initialNumericValue: string | string[];
    if (this.multiSelectEnabled) {
      if (suggestedNumerics.length > 0) {
        // Sugerimos hasta 2 numéricas por defecto para activar comparativo
        initialNumericValue = suggestedNumerics.slice(0, 2);
      } else {
        // Sin sugerencias: empezar con la primera numérica para evitar form inválido inicial
        initialNumericValue = numericCols.length > 0 ? [numericCols[0]] : [];
      }
    } else {
      // Single-select: usar primera sugerencia o primera disponible
      initialNumericValue = suggestedNumerics[0] || numericCols[0] || '';
    }

    this.form = this.fb.group({
      numericColumns: [initialNumericValue, Validators.required],
      labelColumn: [suggestedLabel || '', Validators.required],
    });

    if (true)
      _log('[ColumnSelectorDialogComponent]', {
        ...this.data,
        suggestedLabel,
        suggestedNumerics,
        initialNumericValue,
        multiSelectEnabled: this.multiSelectEnabled,
      });

    this.init = true;

    // Dar foco al selector numérico si ya hay una etiqueta predeterminada
    if (suggestedLabel) {
      setTimeout(() => {
        this.numericSelect?.focus();
      }, 512);
    }
  }

  private findDefaultLabelColumn(): string | null {
    const labelColumns = this.getLabelColumns();
    return labelColumns.find(col => col.toLowerCase().includes('name') || col.toLowerCase().includes('nombre')) || labelColumns[0] || null;
  }

  getLabelColumns(): string[] {
    if (this._cachedLabelColumns !== null) {
      return this._cachedLabelColumns;
    }

    // Primero filtrar columnas no numéricas
    let allLabelColumns = this.data.columns.filter(col => !this.data.numericColumns.includes(col));

    if (allLabelColumns.length === 0) {
      // Todas las columnas son numéricas: preferir las que parezcan IDs como candidatas a etiqueta
      // ya que son más probables de ser dimensiones categóricas (ej: product_id, tienda_id).
      const sorted = [...this.data.columns].sort((a, b) => {
        const aIsId = this.looksLikeIdColumn(a) ? -1 : 1;
        const bIsId = this.looksLikeIdColumn(b) ? -1 : 1;
        return aIsId - bIsId;
      });
      this._cachedLabelColumns = sorted;
      return sorted;
    }

    if (allLabelColumns.length === 1) {
      this._cachedLabelColumns = allLabelColumns;
      return allLabelColumns;
    }

    // Filtrar columnas que parezcan IDs (token "id" delimitado), no por substring genérico
    // — antes "Unidades" se filtraba por contener "id" como substring.
    let filteredColumns = allLabelColumns.filter(col => !this.looksLikeIdColumn(col));
    if (filteredColumns.length === 0) filteredColumns = allLabelColumns;

    // Si tenemos datos filtrados, analizar la variabilidad de los valores
    if (this.data.filteredData?.length > 0) {
      const columnVariability = new Map<string, number>();

      // Calcular cuántos valores únicos tiene cada columna
      filteredColumns.forEach(colName => {
        const uniqueValues = new Set(this.data.filteredData.map(row => String(row[colName]))).size;
        columnVariability.set(colName, uniqueValues);
      });

      // Filtrar columnas que tienen más de un valor único
      const columnsWithVariability = filteredColumns.filter(col => {
        const uniqueCount = columnVariability.get(col);
        return uniqueCount > 1;
      });

      // Si quedan columnas después del filtrado, usarlas
      if (columnsWithVariability.length > 0) {
        this._cachedLabelColumns = columnsWithVariability;
        return columnsWithVariability;
      }
    }

    // Si no hay datos filtrados o todas las columnas tienen valores constantes
    this._cachedLabelColumns = filteredColumns;
    return filteredColumns;
  }

  getNumericColumns(): string[] {
    if (this._cachedNumericColumns !== null) {
      return this._cachedNumericColumns;
    }

    const allNumericColumns = this.data.numericColumns;

    // Si hay más de 1 columna, intentar descartar columnas que parezcan IDs
    // (token "id" delimitado, no por substring — antes "Unidades" se eliminaba por contener "id").
    if (allNumericColumns.length > 1) {
      const filteredColumns = allNumericColumns.filter(col => !this.looksLikeIdColumn(col));
      if (filteredColumns.length > 0) {
        this._cachedNumericColumns = filteredColumns;
        return filteredColumns;
      }
    }

    this._cachedNumericColumns = allNumericColumns;
    return allNumericColumns;
  }

  /**
   * Heurística: la columna parece ser un identificador.
   * Matchea "id" como token delimitado en snake_case o sufijo camelCase, no por substring.
   * - true: "id", "user_id", "id_user", "userId", "productID"
   * - false: "Unidades", "videoId" no — pero sí matcheamos "Id" sufijo camelCase
   *
   * Razón: una búsqueda `includes('id')` filtraba erróneamente palabras como "Unidades"
   * o "Liquido" que contienen las letras "id" como substring.
   */
  private looksLikeIdColumn(col: string): boolean {
    if (!col) return false;
    const lower = col.toLowerCase();
    if (lower === 'id') return true;
    if (lower.startsWith('id_')) return true;
    if (lower.endsWith('_id')) return true;
    if (lower.includes('_id_')) return true;
    // camelCase: termina con "Id" precedido por minúscula (ej: "userId", "productId")
    if (/[a-z]Id$/.test(col)) return true;
    return false;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (!this.form.valid) return;

    // Normalizar al formato { labelColumn, numericColumns: string[] }: la UI puede tener
    // un string (single-select) o un array (multi-select) según `multiSelectEnabled`.
    const raw = this.form.value;
    const numericColumns: string[] = (Array.isArray(raw.numericColumns) ? raw.numericColumns : [raw.numericColumns]).slice(0, 2);

    this.dialogRef.close({
      labelColumn: raw.labelColumn,
      numericColumns,
    });
  }

  private formatColumnTitle(columnName: string): string {
    return _formatColumnTitle(columnName);
  }

  // Translation methods
  getTitle(): string {
    return this.translate.instant('DATA_VISUALIZATION.COLUMN_SELECTOR.TITLE') || 'Seleccionar columnas para el gráfico';
  }

  getLabelColumnText(): string {
    return this.translate.instant('DATA_VISUALIZATION.COLUMN_SELECTOR.LABEL_COLUMN') || 'Columna de etiquetas';
  }

  getNumericColumnText(): string {
    return this.translate.instant('DATA_VISUALIZATION.COLUMN_SELECTOR.NUMERIC_COLUMN') || 'Columna de valores (numérica)';
  }

  getCancelText(): string {
    return this.translate.instant('DATA_VISUALIZATION.COLUMN_SELECTOR.CANCEL') || 'Cancelar';
  }

  getVisualizeText(): string {
    return this.translate.instant('DATA_VISUALIZATION.COLUMN_SELECTOR.VISUALIZE') || 'Visualizar';
  }

  getMultiSeriesHint(): string {
    return (
      this.translate.instant('DATA_VISUALIZATION.COLUMN_SELECTOR.MULTI_SERIES_HINT') ||
      'Podés seleccionar varias columnas para comparar series'
    );
  }
}
